import { useTranslation } from '@pancakeswap/localization'
import dayjs from 'dayjs'
import { useActiveChainId } from '@/hooks/useActiveChainId'
import useTheme from '@/hooks/useTheme'
import { useEffect, useMemo, useState } from 'react'
import isUndefinedOrNull from '@pancakeswap/utils/isUndefinedOrNull'
import BarChart from './components/BarChart/alt'
import LineChart from './components/LineChart/alt'
import Percent from './components/Percent'
import TransactionsTable from './components/TransactionsTable'
import {
  useProtocolChartData,
  useProtocolData,
  useProtocolTransactionData,
  useTopPoolsData,
  useTopTokensData,
} from './hooks'
import { useTransformedVolumeData } from './hooks/chart'
import { VolumeWindow } from './types'
import { getPercentChange } from './utils/data'
import { unixToDate } from './utils/date'
import { formatDollarAmount } from './utils/numbers'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { tokenColumns } from '@/components/explore/table/token/columns'
import { TokenTable } from '@/components/explore/table/token/table'
import { PoolTable } from '@/components/explore/table/pool/table'
import { poolColumns } from '@/components/explore/table/pool/columns'

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { theme } = useTheme()

  const protocolData = useProtocolData()
  const transactionData = useProtocolTransactionData()
  const topTokensData = useTopTokensData()
  const topPoolsData = useTopPoolsData()
  const chartData = useProtocolChartData()
  const { chainId } = useActiveChainId()
  const { t } = useTranslation()

  const [volumeHover, setVolumeHover] = useState<number | undefined>()
  const [liquidityHover, setLiquidityHover] = useState<number | undefined>()
  const [leftLabel, setLeftLabel] = useState<string | undefined>()
  const [rightLabel, setRightLabel] = useState<string | undefined>()
  const now = dayjs()

  useEffect(() => {
    setLiquidityHover(undefined)
    setVolumeHover(undefined)
  }, [chainId])

  useEffect(() => {
    if (liquidityHover === undefined && protocolData) {
      setLiquidityHover(protocolData.tvlUSD)
    }
  }, [liquidityHover, protocolData])

  const formattedTvlData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.tvlUSD,
        }
      })
    }
    return []
  }, [chartData])

  const formattedVolumeData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.volumeUSD,
        }
      })
    }
    return []
  }, [chartData])

  const weeklyVolumeData = useTransformedVolumeData(chartData, 'week')
  const monthlyVolumeData = useTransformedVolumeData(chartData, 'month')
  const [volumeWindow, setVolumeWindow] = useState(VolumeWindow.daily)

  const formattedTokens = useMemo(() => {
    if (topTokensData)
      return Object.values(topTokensData)
        .map((d) => d)
        .filter((d) => !isUndefinedOrNull(d))
        .filter((d) => d.tvlUSD >= 0)
    return []
  }, [topTokensData])

  const poolDatas = useMemo(() => {
    if (topPoolsData)
      return Object.values(topPoolsData)
        .map((p) => p)
        .filter((p) => !isUndefinedOrNull(p))
    return []
  }, [topPoolsData])

  const tvlValue = useMemo(() => {
    return formatDollarAmount(liquidityHover, 2, true)
  }, [liquidityHover])

  console.log(Object.keys(volumeWindow))

  return (
    <div className={'pb-16'}>
      <div className={'grid lg:grid-cols-2 gap-6'}>
        <div className={'box'}>
          <LineChart
            data={formattedTvlData}
            height={200}
            minHeight={300}
            // color={theme.colors.primary}
            value={liquidityHover}
            label={leftLabel}
            setValue={setLiquidityHover}
            setLabel={setLeftLabel}
            topLeft={
              <div className={'flex flex-col gap-1'}>
                <h6>{t('TVL')}</h6>
                <div className={'text-4xl'}>
                  <div className={'tabular-nums'}>{tvlValue}</div>
                </div>
                <div className={'text-xs'}>
                  <div className={'tabular-nums'}>{leftLabel ?? now.format('MMM D, YYYY')} (UTC)</div>
                </div>
              </div>
            }
          />
        </div>
        <div className={'box'}>
          <BarChart
            height={200}
            minHeight={300}
            data={
              volumeWindow === VolumeWindow.monthly
                ? monthlyVolumeData
                : volumeWindow === VolumeWindow.weekly
                ? weeklyVolumeData
                : formattedVolumeData
            }
            color={theme.colors.primary}
            setValue={setVolumeHover}
            setLabel={setRightLabel}
            value={volumeHover}
            label={rightLabel}
            activeWindow={volumeWindow}
            topRight={
              <>
                <Tabs
                  value={volumeWindow.toString()}
                  onValueChange={(value) => setVolumeWindow(Number(value) as VolumeWindow)}
                >
                  <TabsList>
                    {Object.keys(VolumeWindow)
                      .filter((k) => !Number.isNaN(k))
                      .map((window) => (
                        <TabsTrigger value={window.toString()} key={window}>
                          {VolumeWindow[window].toString().charAt(0).toUpperCase()}
                        </TabsTrigger>
                      ))}
                  </TabsList>
                </Tabs>
              </>
            }
            topLeft={
              <div className={'flex flex-col gap-1'}>
                <h6>{t('Volume')}</h6>
                <div className={'text-4xl'}>
                  <div className={'tabular-nums'}>
                    {volumeHover
                      ? formatDollarAmount(volumeHover)
                      : formatDollarAmount(formattedVolumeData[formattedVolumeData.length - 1]?.value, 2)}
                  </div>
                </div>
                <div className={'text-xs'}>
                  <div className={'tabular-nums'}>{rightLabel ?? now.format('MMM D, YYYY')} (UTC)</div>
                </div>
              </div>
            }
          />
        </div>
      </div>
      <div className={'mb-16'}>
        <div className={'box flex items-center gap-6 mt-6 overflow-x-scroll'}>
          <div className={'flex gap-1'}>
            <Typography>{t('Volume 24H')}: </Typography>
            <Typography>{formatDollarAmount(formattedVolumeData[formattedVolumeData.length - 1]?.value)}</Typography>
            <Percent
              value={getPercentChange(
                formattedVolumeData[formattedVolumeData.length - 1]?.value.toString(),
                formattedVolumeData[formattedVolumeData.length - 2]?.value.toString(),
              )}
              wrap
            />
          </div>
          <div className={'flex gap-1'}>
            <Typography>{t('Fees 24H')}: </Typography>
            <Typography>{formatDollarAmount(protocolData?.feesUSD)}</Typography>
            <Percent value={protocolData?.feeChange} wrap />
          </div>
          <div className={'flex gap-1'}>
            <Typography>{t('TVL')}: </Typography>
            <Typography>{formatDollarAmount(protocolData?.tvlUSD)}</Typography>
            <Percent value={protocolData?.tvlUSDChange} wrap />
          </div>
        </div>
      </div>
      <Tabs defaultValue={'tokens'} className={'mt-8'}>
        <TabsList className={'bg-transparent px-0 gap-6'}>
          <TabsTrigger value={'tokens'} className={'px-0'}>
            <Typography variant={'large'}>{t('Tokens')}</Typography>
          </TabsTrigger>
          <TabsTrigger value={'pairs'} className={'px-0'}>
            <Typography variant={'large'}>{t('Pools')}</Typography>
          </TabsTrigger>
          <TabsTrigger value={'transactions'} className={'px-0'}>
            <Typography variant={'large'}>{t('Transactions')}</Typography>
          </TabsTrigger>
        </TabsList>
        <TabsContent value={'tokens'}>
          <TokenTable columns={tokenColumns} data={formattedTokens} />
        </TabsContent>
        <TabsContent value={'pairs'}>
          <PoolTable data={poolDatas} columns={poolColumns} />
        </TabsContent>
        <TabsContent value={'transactions'}>
          {transactionData ? <TransactionsTable transactions={transactionData} /> : null}
        </TabsContent>
      </Tabs>
    </div>
  )
}
